import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Form, Input, DatePicker, Select, Button, Space, Typography, Col, Row, Card, Modal, Checkbox, Collapse, message } from 'antd';
import { UserOutlined, IdcardOutlined, PhoneOutlined, SaveOutlined, FileTextOutlined, PaperClipOutlined, BookOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import config from '../../../config';
import axiosInstance from '../../../utils/axiosConfig';
import GradesSection from './GradesSection';
import AdmissionsList from './AdmissionsList';
import AttachmentsSection from './AttachmentsSection';
import { PRINCIPAL_SUBJECTS } from '../../../config';
import PaymentInfo from './PaymentInfo';
import { HelpButton } from './TooltipHelpers';
import DoiTuongChinhSachUuTienGuide from '../../DoiTuongChinhSachUuTienGuide/DoiTuongChinhSachUuTienGuide';
import KhuVucUuTienGuide from '../../KhuVucUuTienGuide/KhuVucUuTienGuide';
const { Option } = Select;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const StudentProfileForm = ({
    form,
    subjects,
    periods,
    gradeFields,
    submitLoading,
    onFinish,
    initialValues,
    uploadProps,
    uploadLoading,
    uploadProgress,
    uploadStatus,
    fileList,
    attachmentTypes,
    subjectTypes,
    studentProfileData,
    onFileSelect,
    onManualUpload,
    onBatchUpload,
    currentStep = null,  // null means show all (no steps mode)
    externalAttachmentErrors = null  // Pass errors from parent (steps mode)
}) => {
    const prizeValue = Form.useWatch('provincial_prize', form);

    // Auto-clear subject & grade when prize is deselected
    useEffect(() => {
        if (!prizeValue) {
            form.setFieldsValue({ provincial_prize_subject_id: undefined, provincial_prize_grade: undefined });
        }
    }, [prizeValue, form]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isAreaModalVisible, setIsAreaModalVisible] = useState(false);
    const [filteredSubjects, setFilteredSubjects] = useState(subjects);
    const [currentFormValues, setCurrentFormValues] = useState({});
    const [activeCollapsePanels, setActiveCollapsePanels] = useState(['1', '2', '3', '4', '5', '6']);
    const [internalAttachmentErrors, setInternalAttachmentErrors] = useState({});
    const [provinces, setProvinces] = useState([]);
    const [communes, setCommunes] = useState([]);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [loadingCommunes, setLoadingCommunes] = useState(false);
    
    // Use external errors when in steps mode, internal otherwise
    const attachmentErrors = currentStep !== null && externalAttachmentErrors !== null 
        ? externalAttachmentErrors 
        : internalAttachmentErrors;
    const setAttachmentErrors = currentStep !== null && externalAttachmentErrors !== null
        ? () => {} // No-op in steps mode (parent controls errors)
        : setInternalAttachmentErrors;

    // Khi subjects và first_admission_subject_type_id đều có dữ liệu, khởi tạo filteredSubjects cho NV1
    useEffect(() => {
        if (!subjects?.length) return;
        const typeId = initialValues?.first_admission_subject_type_id;
        if (typeId) {
            setFilteredSubjects(subjects.filter(s => s.subject_type_id === typeId));
        } else {
            setFilteredSubjects(subjects);
        }
    }, [subjects, initialValues?.first_admission_subject_type_id]);

    // Force "Khối ngành dự tuyển" (NV1) field để re-map ID -> label sau khi subjectTypes tải xong (edit mode)
    // Cùng vấn đề như commune_id bên dưới: value đã được set qua form.setFieldsValue trước khi
    // subjectTypes (options) sẵn sàng, khiến Select hiển thị trống dù value đã có.
    // Chỉ áp dụng một lần để không đè lên lựa chọn sau đó của user.
    const appliedFirstAdmissionTypeFixRef = useRef(false);
    useEffect(() => {
        if (appliedFirstAdmissionTypeFixRef.current) return;
        const typeId = initialValues?.first_admission_subject_type_id;
        if (subjectTypes?.length && typeId) {
            form.setFieldValue('first_admission_subject_type_id', typeId);
            appliedFirstAdmissionTypeFixRef.current = true;
        }
    }, [subjectTypes, initialValues?.first_admission_subject_type_id, form]);

    // Handle subject type change
    const handleSubjectTypeChange = (value) => {
        if (value) {
            const filtered = subjects.filter(subject => subject.subject_type_id === value);
            setFilteredSubjects(filtered);
        } else {
            setFilteredSubjects(subjects);
        }
        // Clear the first admission selection when type changes
        form.setFieldValue('first_admission_id', undefined);
    };

    // Handle province change
    const handleProvinceChange = async (value) => {
        form.setFieldValue('commune_id', undefined); // Clear commune selection
        setCommunes([]); // Clear communes list
        
        if (value) {
            await fetchCommunesByProvince(value);
        }
    };

    // Fetch provinces
    const fetchProvinces = async () => {
        try {
            setLoadingProvinces(true);
            const response = await axiosInstance.get(`/api/v1/provinces`);
            setProvinces(response.data || []);
        } catch (error) {
            console.error('Error fetching provinces:', error);
            message.error('Không thể tải danh sách tỉnh/thành phố');
        } finally {
            setLoadingProvinces(false);
        }
    };

    // Fetch communes by province
    const fetchCommunesByProvince = async (provinceId) => {
        try {
            setLoadingCommunes(true);
            const response = await axiosInstance.get(`/api/v1/communes/by-province/${provinceId}`);
            const communesData = response.data || [];
            setCommunes(communesData);
            
            // After communes loaded, restore commune_id from form if exists
            setTimeout(() => {
                const currentCommuneId = form.getFieldValue('commune_id');
                if (currentCommuneId && communesData.length > 0) {
                    // Re-set the same value to trigger Select to map ID -> label
                    form.setFieldValue('commune_id', currentCommuneId);
                }
            }, 50);
        } catch (error) {
            console.error('Error fetching communes:', error);
            message.error('Không thể tải danh sách quận/huyện');
        } finally {
            setLoadingCommunes(false);
        }
    };

    // Track form values changes to update studentProfileData for AttachmentsSection
    useEffect(() => {
        const formValues = form.getFieldsValue();
        setCurrentFormValues({
            ...studentProfileData,
            ...formValues
        });
    }, [form, studentProfileData]);

    // Fetch provinces on component mount
    useEffect(() => {
        fetchProvinces();
    }, []);

    // Fetch communes when initialValues has province_id
    useEffect(() => {
        if (initialValues?.province_id) {
            fetchCommunesByProvince(initialValues.province_id);
        }
    }, [initialValues?.province_id]);

    // Force commune field update after communes are loaded (for edit mode)
    useEffect(() => {
        if (communes.length > 0) {
            const communeId = form.getFieldValue('commune_id');
            if (communeId) {
                // Re-set the same value to trigger Select to map ID -> label
                form.setFieldValue('commune_id', communeId);
            }
        }
    }, [communes, form]);

    // Clear attachment errors when files are uploaded
    useEffect(() => {
        if (Object.keys(attachmentErrors).length > 0) {
            const updatedErrors = { ...attachmentErrors };
            let hasChanges = false;
            
            Object.keys(updatedErrors).forEach(typeId => {
                if (fileList[typeId] && fileList[typeId].length > 0) {
                    delete updatedErrors[typeId];
                    hasChanges = true;
                }
            });
            
            if (hasChanges) {
                setAttachmentErrors(updatedErrors);
            }
        }
    }, [fileList, attachmentErrors]);

    // Helper function to check if attachment is required
    const isAttachmentRequired = (typeCode, formValues) => {
        if (typeCode === 'TRANSCRIPT') {
            const graduationYear = formValues?.graduation_year;
            // Required if graduation year is before 2026
            return graduationYear && parseInt(graduationYear) < 2026;
        }
        if (typeCode === 'IELTS') {
            // Required if IELTS score is entered
            return formValues?.ielts_grade && formValues.ielts_grade !== '';
        }
        if (typeCode === 'PRIORITY') {
            // Chỉ bắt buộc khi đã điền ĐẦY ĐỦ thông tin giải tỉnh (loại giải, môn, và lớp)
            return formValues?.provincial_prize && 
                   formValues.provincial_prize !== '' &&
                   formValues?.provincial_prize_subject_id && 
                   formValues.provincial_prize_subject_id !== '' &&
                   formValues?.provincial_prize_grade && 
                   formValues.provincial_prize_grade !== '';
        }
        // FRONT_ID_CARD and BACK_ID_CARD are always required
        return typeCode === 'FRONT_ID_CARD' || typeCode === 'BACK_ID_CARD';
    };

    // Validate attachments before submission
    const validateAttachments = () => {
        const formValues = form.getFieldsValue();
        const errors = [];
        
        attachmentTypes.forEach(type => {
            // Check if this attachment should be displayed
            if (type.code === 'IELTS' && (!formValues.ielts_grade || formValues.ielts_grade === '')) {
                return; // Skip IELTS if not applicable
            }
            // Skip PRIORITY nếu chưa điền ĐẦY ĐỦ thông tin giải tỉnh
            if (type.code === 'PRIORITY' && 
                (!formValues.provincial_prize || formValues.provincial_prize === '' ||
                 !formValues.provincial_prize_subject_id || formValues.provincial_prize_subject_id === '' ||
                 !formValues.provincial_prize_grade || formValues.provincial_prize_grade === '')) {
                return; // Skip PRIORITY if not fully filled
            }

            // Check if required attachment is missing
            if (isAttachmentRequired(type.code, formValues)) {
                const hasFile = fileList[type.id] && fileList[type.id].length > 0;
                if (!hasFile) {
                    errors.push(`Vui lòng tải lên ${type.name}`);
                }
            }
        });

        return errors;
    };

    // Handle form validation failed
    const onFinishFailed = (errorInfo) => {
        console.log('Validation failed:', errorInfo);
        
        // Validate attachments and set errors
        const errors = {};
        const formValues = form.getFieldsValue();
        
        attachmentTypes.forEach(type => {
            // Skip if not applicable
            if (type.code === 'IELTS' && (!formValues.ielts_grade || formValues.ielts_grade === '')) {
                return;
            }
            // Skip PRIORITY nếu chưa điền ĐẦY ĐỦ thông tin giải tỉnh
            if (type.code === 'PRIORITY' && 
                (!formValues.provincial_prize || formValues.provincial_prize === '' ||
                 !formValues.provincial_prize_subject_id || formValues.provincial_prize_subject_id === '' ||
                 !formValues.provincial_prize_grade || formValues.provincial_prize_grade === '')) {
                return;
            }

            // Check if required
            if (isAttachmentRequired(type.code, formValues)) {
                const hasFile = fileList[type.id] && fileList[type.id].length > 0;
                if (!hasFile) {
                    errors[type.id] = `Vui lòng tải lên ${type.name}`;
                }
            }
        });
        
        setAttachmentErrors(errors);
        
        const errorFields = errorInfo.errorFields;
        
        if (errorFields && errorFields.length > 0) {
            // Focus on first error field
            const firstErrorField = errorFields[0].name;
            form.scrollToField(firstErrorField, {
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
        } else if (Object.keys(errors).length > 0) {
            // If only attachment errors, scroll to attachments
            const attachmentSection = document.getElementById('attachments-section');
            if (attachmentSection) {
                attachmentSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    // Custom onFinish handler to validate attachments
    const handleFormSubmit = (values) => {
        // Validate attachments
        const attachmentErrorsList = validateAttachments();
        
        if (attachmentErrorsList.length > 0) {
            // Set errors for display in cards
            const errors = {};
            const formValues = form.getFieldsValue();
            
            attachmentTypes.forEach(type => {
                // Skip if not applicable
                if (type.code === 'IELTS' && (!formValues.ielts_grade || formValues.ielts_grade === '')) {
                    return;
                }
                // Skip PRIORITY nếu chưa điền ĐẦY ĐỦ thông tin giải tỉnh
                if (type.code === 'PRIORITY' && 
                    (!formValues.provincial_prize || formValues.provincial_prize === '' ||
                     !formValues.provincial_prize_subject_id || formValues.provincial_prize_subject_id === '' ||
                     !formValues.provincial_prize_grade || formValues.provincial_prize_grade === '')) {
                    return;
                }

                // Check if required
                if (isAttachmentRequired(type.code, formValues)) {
                    const hasFile = fileList[type.id] && fileList[type.id].length > 0;
                    if (!hasFile) {
                        errors[type.id] = `Vui lòng tải lên ${type.name}`;
                    }
                }
            });
            
            setAttachmentErrors(errors);
            
            // Scroll to attachments section
            const attachmentSection = document.getElementById('attachments-section');
            if (attachmentSection) {
                attachmentSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            
            return;
        }
        
        // Clear errors if validation passes
        setAttachmentErrors({});
        
        // If validation passes, call the original onFinish
        onFinish(values);
    };

    // Handle form values change to update currentFormValues for AttachmentsSection
    // Only update when fields that affect attachments change (for performance)
    const handleValuesChange = useCallback((changedValues, allValues) => {
        // Fields that affect attachment visibility/requirements
        const attachmentRelatedFields = [
            'ielts_grade',
            'provincial_prize',
            'provincial_prize_subject_id',
            'provincial_prize_grade',
            'graduation_year'
        ];
        
        // Check if any changed field affects attachments
        const shouldUpdate = Object.keys(changedValues).some(field => 
            attachmentRelatedFields.includes(field)
        );
        
        if (shouldUpdate) {
            // When prize is cleared, also wipe subject/grade from currentFormValues
            // (form.setFieldsValue used in useEffect doesn't re-trigger onValuesChange)
            const merged = { ...studentProfileData, ...allValues };
            if (!merged.provincial_prize) {
                merged.provincial_prize_subject_id = undefined;
                merged.provincial_prize_grade = undefined;
            }
            setCurrentFormValues(merged);
        }
    }, [studentProfileData]);

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={handleFormSubmit}
            onFinishFailed={onFinishFailed}
            onValuesChange={handleValuesChange}
            scrollToFirstError={{
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            }}
            validateMessages={{
                required: '${label} là bắt buộc!',
                types: {
                    email: '${label} không hợp lệ!',
                    number: '${label} phải là số!',
                },
                pattern: {
                    mismatch: '${label} không đúng định dạng!'
                }
            }}
            requiredMark={(label, { required }) => (
                <>
                    {label}
                    {required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
                </>
            )}
        >
            {/* Step 0: Personal Information */}
            <Card
                title={
                    <Space>
                        <UserOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Bước 1: Thông tin cá nhân & Khu vực ưu tiên
                        </Typography.Title>
                    </Space>
                }
                style={{ 
                    maxWidth: 1200, 
                    margin: '20px auto',
                    display: (currentStep === null || currentStep === 0) ? 'block' : 'none'
                }} 
                className="step-section"
            >
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Form.Item
                            name="student_code"
                            label="Mã hồ sơ"
                        >
                            <Input
                                prefix={<UserOutlined />}
                                placeholder="Mã hồ sơ"
                                disabled
                                readOnly
                                className="read-only-input"
                            />
                        </Form.Item>
                    </Col>
                    {/* <Col span={12}>
                        <Form.Item
                            name="period_id"
                            label="Đợt tuyển sinh"
                            rules={[{ required: true, message: 'Vui lòng chọn đợt tuyển sinh!' }]}
                        >
                            <Select placeholder="Chọn đợt tuyển sinh">
                                {periods.map(period => (
                                    <Option key={period.id} value={period.id}>
                                        {period.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col> */}
                </Row>

                {/* Personal Information Section */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="full_name"
                            label="Họ và tên"
                            rules={[
                                { required: true, message: 'Vui lòng nhập họ và tên!' },
                                { min: 3, message: 'Họ tên phải có ít nhất 3 ký tự!' },
                                { max: 100, message: 'Họ tên không được quá 100 ký tự!' }
                            ]}
                            validateTrigger="onBlur"                            
                        >
                            <Input prefix={<UserOutlined />} placeholder="Nhập họ và tên" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="date_of_birth"
                            label="Ngày sinh"
                            rules={[
                                { required: true, message: 'Vui lòng chọn ngày sinh!' },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        const age = dayjs().diff(value, 'year');
                                        if (age < 15) {
                                            return Promise.reject('Tuổi phải từ 15 trở lên!');
                                        }
                                        if (age > 100) {
                                            return Promise.reject('Ngày sinh không hợp lệ!');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            validateTrigger="onChange"
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                placeholder="Chọn ngày sinh"
                                maxDate={dayjs('2008-12-31')}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Identification Section */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="identification"
                            label="Số CCCD"
                            rules={[
                                { required: true, message: 'Vui lòng nhập số CCCD' },
                                { pattern: /^\d{12,12}$/, message: 'Số CCCD phải có đúng 12 chữ số!' }
                            ]}
                            validateTrigger="onBlur"
                            
                            extra="Vui lòng kiểm tra kỹ số CCCD trước khi nhập"
                        >
                            <Input prefix={<IdcardOutlined />} placeholder="Nhập số CCCD" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="identification_verify"
                            label="Nhập lại CCCD"
                            dependencies={['identification']}
                            validateTrigger="onBlur"
                            rules={[
                                { required: true, message: 'Vui lòng xác nhận số CCCD' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('identification') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject('​Số CCCD xác nhận không khớp! Vui lòng kiểm tra lại.');
                                    }
                                })
                            ]}
                            extra="Nhập lại để đảm bảo chính xác"
                        >
                            <Input
                                prefix={<IdcardOutlined />}
                                placeholder="Nhập lại số CCCD/CMND"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    {/* Phone Numbers */}
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="student_phone_number"
                            label="Điện thoại thí sinh"
                            rules={[
                                { required: true, message: 'Vui lòng nhập số điện thoại!' },
                                { pattern: /^[0-9]{10}$/, message: 'Số điện thoại phải có 10 chữ số!' }
                            ]}
                            validateTrigger="onBlur"
                            
                        >
                            <Input prefix={<PhoneOutlined />} placeholder="Nhập số điện thoại" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="parents_phone_number"
                            label="Điện thoại phụ huynh"
                            rules={[
                                { required: true, message: 'Vui lòng nhập số điện thoại!' },
                                { pattern: /^[0-9]{10}$/, message: 'Số điện thoại phải có 10 chữ số!' }
                            ]}
                            validateTrigger="onBlur"
                            
                        >
                            <Input prefix={<PhoneOutlined />} placeholder="Nhập số điện thoại phụ huynh" />
                        </Form.Item>
                    </Col>
                </Row>

                

                {/* Province and Commune Section */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="province_id"
                            label="Tỉnh/Thành phố"
                            rules={[{ required: true, message: 'Vui lòng chọn tỉnh/thành phố!' }]}
                            validateTrigger="onChange"
                        >
                            <Select
                                placeholder="Chọn tỉnh/thành phố"
                                showSearch
                                loading={loadingProvinces}
                                options={provinces.map(province => ({
                                    label: province.name,
                                    value: province.id
                                }))}
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={handleProvinceChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="commune_id"
                            label="Xã/Phường"
                            rules={[{ required: true, message: 'Vui lòng chọn Xã/Phường!' }]}
                            validateTrigger="onChange"
                        >
                            <Select
                                key={`commune-select-${communes.length}`}
                                placeholder="Chọn Xã/Phường"
                                showSearch
                                loading={loadingCommunes}
                                disabled={!form.getFieldValue('province_id')}
                                options={communes.map(commune => ({
                                    label: commune.name,
                                    value: commune.id
                                }))}
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Contact Information Section */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24}>
                        <Form.Item
                            name="contact_address"
                            rules={[
                                { required: true, message: 'Vui lòng nhập địa chỉ liên hệ!' },
                                { min: 10, message: 'Địa chỉ quá ngắn, vui lòng nhập đầy đủ hơn!' },
                                { max: 500, message: 'Địa chỉ không được quá 500 ký tự!' }
                            ]}
                            validateTrigger="onBlur"
                            
                            label="Địa chỉ liên hệ"
                        >
                            <Input.TextArea 
                                placeholder="Nhập địa chỉ liên hệ cụ thể (Số nhà, đường, phường/xã, quận/huyện, tỉnh/thành phố)" 
                                rows={3}
                                showCount
                                maxLength={500}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="graduation_year"
                            label="Năm tốt nghiệp"
                            rules={[
                                { required: true, message: 'Vui lòng chọn năm tốt nghiệp!' },
                            ]}
                            validateTrigger="onChange"
                            
                        >
                            <Select placeholder="Chọn năm tốt nghiệp">
                                <Select.Option value="2026">2026</Select.Option>
                                <Select.Option value="2025">2025</Select.Option>
                                <Select.Option value="before_2025">Trước 2025</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="admission_area"
                            label={
                                <Space>
                                    Khu vực ưu tiên
                                    <HelpButton 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsAreaModalVisible(true);
                                        }}
                                    />
                                </Space>
                            }
                            rules={[{ required: true, message: 'Vui lòng chọn khu vực ưu tiên!' }]}
                            validateTrigger="onChange"
                        >
                            <Select placeholder="Chọn khu vực">
                                <Option value="KV1">KV1</Option>
                                <Option value="KV2">KV2</Option>
                                <Option value="KV2-NT">KV2-NT</Option>
                                <Option value="KV3">KV3</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="admission_group"
                            label={
                                <Space>
                                    Đối tượng ưu tiên (Nếu có)
                                    <HelpButton 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsModalVisible(true);
                                        }}
                                    />
                                </Space>
                            }
                        >
                            <Select placeholder="Chọn đối tượng ưu tiên" allowClear>
                                {[...Array(6)].map((_, i) => (
                                    <Option key={i + 1} value={`0${i + 1}`}>{`0${i + 1}`}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            {/* Step 1: IELTS, Awards & Grade Rankings */}
            <Card
                title={
                    <Space>
                        <BookOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Bước 2: IELTS, Thành tích & Xếp loại học lực
                        </Typography.Title>
                    </Space>
                }
                style={{ 
                    maxWidth: 1200, 
                    margin: '20px auto',
                    display: (currentStep === null || currentStep === 1) ? 'block' : 'none'
                }} 
                className="step-section"
            >
                {/* IELTS Section */}
                <Typography.Title level={5} style={{ marginBottom: 16 }}>
                    Chứng chỉ IELTS
                </Typography.Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24}>
                        <Form.Item
                            name="ielts_grade"
                            label="Điểm IELTS (Thí sinh chỉ nhập điểm IELTS khi đạt từ 4.5 trở lên)"
                            extra={
                                <Text type="warning" style={{ fontSize: '13px' }}>
                                    <strong>Lưu ý:</strong> Nếu có điểm IELTS, thí sinh cần nộp ảnh chứng chỉ ở Bước 4
                                </Text>
                            }
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value || value === '') {
                                            return Promise.resolve();
                                        }
                                        
                                        const score = parseFloat(value);
                                        if (isNaN(score)) {
                                            return Promise.reject('Điểm IELTS phải là số hợp lệ!');
                                        }
                                        
                                        const validScores = [4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0];
                                        if (!validScores.includes(score)) {
                                            return Promise.reject('Điểm IELTS chỉ được nhập các giá trị: 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0!');
                                        }
                                        
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input
                                placeholder="Nhập điểm IELTS (nếu có, ví dụ: 6.5 hoặc 65)"
                                allowClear
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Only allow numbers and one decimal point
                                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                        form.setFieldValue('ielts_grade', value);
                                    }
                                }}
                                onBlur={(e) => {
                                    const value = e.target.value.trim();
                                    if (value === '') return;
                                    
                                    const validScores = [4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0];
                                    
                                    // Kiểm tra nếu là số nguyên (không có dấu chấm)
                                    if (/^\d+$/.test(value)) {
                                        const numValue = parseInt(value);
                                        
                                        // Nếu >= 10 và <= 90 thì chia cho 10 (ví dụ 65 -> 6.5)
                                        if (numValue >= 45 && numValue <= 90) {
                                            const convertedValue = parseFloat((numValue / 10).toFixed(1));
                                            // Only set if it's a valid IELTS score
                                            if (validScores.includes(convertedValue)) {
                                                form.setFieldValue('ielts_grade', convertedValue.toFixed(1));
                                            }
                                        }
                                        // Nếu < 10 thì thêm .0 (ví dụ 6 -> 6.0)
                                        else if (numValue >= 4 && numValue <= 9) {
                                            const convertedValue = parseFloat(numValue.toFixed(1));
                                            if (validScores.includes(convertedValue)) {
                                                form.setFieldValue('ielts_grade', convertedValue.toFixed(1));
                                            }
                                        }
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Awards Section */}
                <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
                    Giải thưởng học sinh giỏi cấp tỉnh/thành phố
                </Typography.Title>
                <Text type="warning" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                    Chỉ chấp nhận giải thưởng đối với các môn: Toán, Vật lý, Hóa học, Tiếng Anh, Ngữ Văn, Tin học. Nếu có nhiều giải, chỉ nhập giải cao nhất.
                </Text>
                <Card
                    size="small"
                    style={{ marginBottom: 8, background: prizeValue ? '#f6ffed' : '#fafafa', borderColor: prizeValue ? '#b7eb8f' : '#d9d9d9' }}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="provincial_prize"
                                label="Giải thưởng"
                            >
                                <Select placeholder="Không có / Chọn giải" allowClear>
                                    <Option value="first">Giải Nhất</Option>
                                    <Option value="second">Giải Nhì</Option>
                                    <Option value="third">Giải Ba</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="provincial_prize_subject_id"
                                label="Môn đạt giải"
                                rules={[{ required: !!prizeValue, message: 'Vui lòng chọn môn đạt giải' }]}
                            >
                                <Select
                                    placeholder={prizeValue ? 'Chọn môn' : '— Chọn mức giải trước —'}
                                    allowClear
                                    disabled={!prizeValue}
                                >
                                    {Object.entries(PRINCIPAL_SUBJECTS).map(([key, label]) => (
                                        <Option key={key} value={key}>{label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="provincial_prize_grade"
                                label="Năm học đạt giải"
                                rules={[{ required: !!prizeValue, message: 'Vui lòng chọn năm học đạt giải' }]}
                            >
                                <Select
                                    placeholder={prizeValue ? 'Chọn năm' : '— Chọn mức giải trước —'}
                                    allowClear
                                    disabled={!prizeValue}
                                >
                                    <Option value="10">Lớp 10</Option>
                                    <Option value="11">Lớp 11</Option>
                                    <Option value="12">Lớp 12</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Grade Rankings Section */}
                <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                    Xếp loại học lực
                </Typography.Title>

                <Row gutter={[16, 16]}>
                    {/* Grade Rankings */}
                    {[10, 11, 12].map(grade => (
                        <Col xs={24} sm={12} md={8} key={grade}>
                            <Form.Item
                                key={grade}
                                name={`grade_${grade}_rank`}
                                label={`Kết quả học tập lớp ${grade}`}
                                rules={[{ required: true, message: 'Vui lòng chọn xếp loại!' }]}
                                validateTrigger="onChange"
                            >
                                <Select placeholder="Chọn xếp loại">
                                    <Option value="other">Khác</Option>
                                    <Option value="excellent">Tốt/Giỏi</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    ))}
                </Row>

                {/* Checkbox Section for HSA, TSA, SPT */}
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col span={24}>
                        <Typography.Title level={5} style={{ marginBottom: 16 }}>
                            Thí sinh đã tham gia các kỳ thi đánh giá năng lực 2026 ? (Chọn nếu đã hoặc sẽ tham dự)
                        </Typography.Title>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="is_HSA"
                            valuePropName="checked"
                        >
                            <Checkbox>Kỳ thi Đánh giá năng lực - ĐHQG Hà Nội (HSA)</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="is_TSA"
                            valuePropName="checked"
                        >
                            <Checkbox>Kỳ thi Đánh giá tư duy - Đại học Bách khoa Hà Nội (TSA)</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            name="is_SPT"
                            valuePropName="checked"
                        >
                            <Checkbox>Kỳ thi Đánh giá năng lực - Trường Đại học Sư phạm Hà Nội (SPT)</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Text type="warning">
                            Lưu ý: Nhà trường sẽ hỗ trợ đồng bộ điểm từ hệ thống của Quốc gia cho thí sinh để xét.
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* Step 2: Admission Preferences Section */}
            <Card
                title={
                    <Space>
                        <FileTextOutlined style={{ fontSize: 20, color: '#ff9800' }} />
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Bước 3: Nguyện vọng và điểm học tập bậc THPT
                        </Typography.Title>
                        <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                            (Thí sinh có thể chọn tối đa 5 nguyện vọng)
                        </Typography.Text>
                    </Space>
                }
                style={{ 
                    maxWidth: 1200, 
                    margin: '20px auto',
                    display: (currentStep === null || currentStep === 2) ? 'block' : 'none'
                }} 
                className="step-section"
            >
                <Typography.Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16 }}>Nguyện vọng 1</Typography.Text>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={10}>
                        <Form.Item
                            name="first_admission_subject_type_id"
                            label="Khối ngành dự tuyển"
                        >
                            <Select
                                placeholder="Chọn Khối ngành dự tuyển"
                                onChange={handleSubjectTypeChange}
                                options={subjectTypes.map(type => ({
                                    value: type.id,
                                    label: type.name
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={14}>
                        <Form.Item
                            name="first_admission_id"
                            label="Ngành/chuyên ngành"
                            rules={[{ required: true, message: 'Vui lòng chọn nguyện vọng dự kiến!' }]}
                            validateTrigger="onChange"
                        >
                            <Select
                                placeholder="Chọn ngành/chuyên ngành"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={filteredSubjects.map(subject => ({
                                    value: subject.id,
                                    label: `${subject.name} - ${subject.code}`
                                }))}
                                disabled={!form.getFieldValue('first_admission_subject_type_id')}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <AdmissionsList subjects={subjects} form={form} initialAdmissions={initialValues?.admissions || []} />
            </Card>

            {/* Grades Section - Only show in Step 3 */}
            <Card 
                style={{ 
                    maxWidth: 1200, 
                    margin: '20px auto',
                    display: (currentStep === null || currentStep === 2) ? 'block' : 'none'
                }}
                className="step-section"
            >
                <Text type="danger" style={{
                    display: 'block',
                    marginBottom: '16px',
                    padding: '12px',
                    background: '#fff2e8',
                    border: '1px solid #ffbb96',
                    borderRadius: '4px',
                    fontSize: '14px',
                    lineHeight: '1.6'
                }}>
                    <strong>Lưu ý:</strong> Thí sinh có tổng điểm 03 môn thi kỳ thi tốt nghiệp THPT (kỳ thi trung học nghề đối với đối tượng trung học nghề) các năm theo tổ hợp xét tuyển (hoặc sử dụng điểm thi môn Toán, Ngữ văn và một môn thi khác) đạt tối thiểu 15,00 điểm theo thang điểm 30, áp dụng đối với thí sinh thi tốt nghiệp từ năm 2026
                </Text>
                <GradesSection parentForm={form} />
            </Card>

            {/* Step 3: Attachments Section - integrated into the form */}
            <Card
                id="attachments-section"
                title={
                    <Space>
                        <PaperClipOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Tải lên minh chứng
                        </Typography.Title>
                        <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                            (Bước cuối cùng)
                        </Typography.Text>
                    </Space>
                }
                style={{ 
                    maxWidth: 1200, 
                    margin: '20px auto',
                    display: (currentStep === null || currentStep === 3) ? 'block' : 'none',
                    border: '2px solid #722ed1',
                    boxShadow: '0 2px 8px rgba(114, 46, 209, 0.1)'
                }} 
                className="step-section"
            >
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                        Vui lòng tải lên các minh chứng theo yêu cầu dưới đây. Các trường đánh dấu <span style={{ color: '#ff4d4f' }}>*</span> là bắt buộc.
                    </Typography.Paragraph>
                    <AttachmentsSection
                        attachmentTypes={attachmentTypes}
                        uploadProps={uploadProps}
                        uploadLoading={uploadLoading}
                        uploadProgress={uploadProgress}
                        uploadStatus={uploadStatus}
                        fileList={fileList}
                        studentProfileData={currentFormValues}
                        onFileSelect={onFileSelect}
                        onManualUpload={onManualUpload}
                        onBatchUpload={onBatchUpload}
                        validationErrors={attachmentErrors}
                    />
            </Card>

            {/* Modal cho Khu vực ưu tiên */}
            <Modal
                title="Hướng dẫn: Khu vực ưu tiên"
                width={1000}
                maskClosable={true}
                closable={true}
                centered={true}
                open={isAreaModalVisible}
                cancelButtonProps={{ style: { display: 'none' } }}
                okText="Đóng"
                onOk={() => setIsAreaModalVisible(false)}
                onCancel={() => setIsAreaModalVisible(false)}
            >
                <KhuVucUuTienGuide />
            </Modal>

            {/* Modal cho Đối tượng ưu tiên */}
            <Modal
                title="Chi tiết đầy đủ về Đối tượng ưu tiên"
                width={1000}
                maskClosable={true}
                closable={true}
                centered={true}
                open={isModalVisible}
                cancelButtonProps={{ style: { display: 'none' } }}
                okText="Đóng"
                onOk={() => setIsModalVisible(false)}
                onCancel={() => setIsModalVisible(false)}
            >
                <DoiTuongChinhSachUuTienGuide />
            </Modal>


            {/* Submit button - only show when not in steps mode */}
            <div style={{ display: currentStep === null ? 'flex' : 'none', justifyContent: 'center', marginTop: 20 }}>
                <Form.Item>
                    <Button
                        icon={<SaveOutlined />}
                        type="primary"
                        htmlType="submit"
                        loading={submitLoading}
                        block
                        size="large"
                        disabled={submitLoading && !form.getFieldValue('agreement')}
                        style={{ marginTop: 20, maxWidth: '200px', margin: '0 auto' }}
                    >
                        {submitLoading ? 'Đang xử lý...' : 'Cập nhật thông tin'}
                    </Button>
                </Form.Item>
            </div>

        </Form>
    );
};

// Add some styles
const styles = `
.attachment-section {
    margin-bottom: 24px;
    padding: 24px;
    background: #fafafa;
    border-radius: 8px;
}

.required-mark {
    color: #ff4d4f;
    margin-left: 4px;
}

.attachment-upload {
    .ant-upload {
        width: 128px;
        height: 128px;
    }
}
`;

// Add styles to document
// if (typeof document !== 'undefined') {
//     const styleElement = document.createElement('style');
//     styleElement.innerHTML = styles;
//     document.head.appendChild(styleElement);
// }

export default React.memo(StudentProfileForm); 